import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'v5/design-system/Button';
import { cn } from 'v5/platform/dom/cn';

export interface EntityEmptyStateProps {
  icon?: ReactNode;
  title: string;
  /** Message encouraging user to create (shown when encourageCreate=true, default) */
  message: string;
  /** Message when not encouraging create (e.g., viewing archived items) */
  passiveMessage?: string;
  /** Whether to show the encouraging message (default: true). Set to false for archived views etc. */
  encourageCreate?: boolean;
  /** Whether filters can be cleared (shows "Clear filters" button) */
  clearable?: boolean;
  /** Called when user clicks "Clear filters" */
  onClear?: () => void;
  /** Extra control under the message (e.g. Go to last page). */
  action?: ReactNode;
  className?: string;
}

export function EntityEmptyState({
  icon,
  title,
  message,
  passiveMessage,
  encourageCreate = true,
  clearable = false,
  onClear,
  action,
  className
}: EntityEmptyStateProps) {
  const { t } = useTranslation('patterns', { keyPrefix: 'EntityEmptyState' });
  const passiveLabel = passiveMessage ?? t('nothingHereYet');
  const displayMessage: string = clearable
    ? t('noResultsMatchFilters')
    : encourageCreate
      ? message
      : passiveLabel;

  return (
    <div
      className={cn(
        'text-center h-full flex flex-col justify-center items-center py-12 text-fg-default',
        className
      )}
    >
      {/* TODO: Add icon */}
      {/* {icon && (
          <div className="mb-4 flex justify-center  [&_svg]:size-12">{icon}</div>
        )} */}
      <h3 className="text-lg font-medium mb-2">{title}</h3>
      <p className="text-fg-muted mb-4">{displayMessage}</p>
      {action}
      {clearable && onClear && (
        <Button onClick={onClear} size="sm" variant="link">
          {t('clearFilters')}
        </Button>
      )}
    </div>
  );
}

export type OutOfRangePageEmptyStateProps = {
  onGoToLastPage: () => void;
  className?: string;
};

/** Empty state when URL page is past the last page for the current total. */
export function OutOfRangePageEmptyState({
  onGoToLastPage,
  className
}: OutOfRangePageEmptyStateProps) {
  const { t } = useTranslation('patterns', { keyPrefix: 'EntityEmptyState' });

  return (
    <EntityEmptyState
      title={t('pageOutOfRangeTitle')}
      message={t('pageOutOfRangeMessage')}
      className={className}
      action={
        <Button onClick={onGoToLastPage} size="sm" variant="primaryAlt">
          {t('goToLastPage')}
        </Button>
      }
    />
  );
}
